import { Logger } from './logger';
import { LogLevel } from './logger/models';

export const copyToClipboard = (
    text: string,
    callback?: () => unknown,
): void => {
    if (window.navigator.clipboard) {
        window.navigator.clipboard
            .writeText(text)
            .then(callback)
            .catch((error) => {
                Logger.log(
                    'Unable to save navigator clipboard',
                    error,
                    LogLevel.ERROR,
                );
            });
    } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;

        // Avoid scrolling to bottom
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.position = 'fixed';

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        try {
            document.execCommand?.('copy')
                ? callback?.()
                : Logger.log(
                      'Unable to save to clipboard.',
                      undefined,
                      LogLevel.ERROR,
                  );
        } catch (err) {
            Logger.log(
                'Unable to save to clipboard. Error thrown',
                undefined,
                LogLevel.ERROR,
            );
        }
        document.body.removeChild(textarea);
    }
};

/**
 * Copy text to the clipboard using a Promise that resolves later.
 *
 * This must be called synchronously inside a user gesture (e.g. click handler).
 * Safari/iOS require this pattern for any async clipboard write; without it,
 * `navigator.clipboard.writeText` called after `await` is rejected silently
 * because the transient activation is gone by then.
 *
 * Returns true if the async write was scheduled, false if we had to fall back.
 */
export const copyToClipboardAsync = (
    textPromise: Promise<string>,
): Promise<void> => {
    try {
        if (
            typeof ClipboardItem !== 'undefined' &&
            window.navigator.clipboard &&
            'write' in window.navigator.clipboard
        ) {
            const blobPromise = textPromise.then(
                (text) => new Blob([text], { type: 'text/plain' }),
            );
            // Some TS lib versions type ClipboardItem values as Blob only;
            // Safari accepts a Promise<Blob> here at runtime.
            const ClipboardItemCtor = ClipboardItem as unknown as new (
                items: Record<string, Promise<Blob> | Blob>,
            ) => ClipboardItem;
            const item = new ClipboardItemCtor({ 'text/plain': blobPromise });
            return window.navigator.clipboard.write([item]).catch((error) => {
                Logger.log(
                    'Unable to save navigator clipboard (async)',
                    error,
                    LogLevel.ERROR,
                );
            });
        }
    } catch (error) {
        Logger.log(
            'ClipboardItem async write failed',
            { error: error instanceof Error ? error.message : String(error) },
            LogLevel.ERROR,
        );
    }
    // Fallback: caller should await the promise and copy synchronously.
    return textPromise
        .then((text) => copyToClipboard(text))
        .catch(() => undefined);
};

export const copyDoneeIdToClipboard = (hashedId?: string) => {
    if (!hashedId) return;
    copyToClipboard(`https://givelify.com/dl/donee/${hashedId}`);
};
